import { Component, OnDestroy, ChangeDetectionStrategy, computed, effect, inject, output, input, signal, untracked } from '@angular/core';
import { Subscription } from 'rxjs';
import { hasVariableToken } from '@app/_helpers/variable-tokens';
import { VariableScope } from '@app/services/variable.service';
import { SbTextConditionService, TextConditionContext } from './sb-text-condition.service';
import type { SbTextConditionTarget } from './sb-text-condition-button.component';
import { quote, TextBlock, TextProblem } from './text-condition-grammar';

interface BlockSummary {
  block: TextBlock;
  text: string;
}

/** What the hint says about a text: its first problem, or its conditional blocks in words. */
interface TextHint {
  problem: TextProblem | null;
  problemCount: number;
  summaries: BlockSummary[];
  moreBlocks: number;
  conditionalOnly: string[];
}

const NO_HINT: TextHint = {problem: null, problemCount: 0, summaries: [], moreBlocks: 0, conditionalOnly: []};

/** At most this many blocks are summarised under a field; the rest are counted. */
const MAX_SUMMARIES = 3;

/**
 * Under a plain-text field: what is wrong with its `{% if %}` syntax (the same rules the server
 * renders by), or else each conditional text in plain words with an Edit link.
 */
@Component({
  selector: 'app-sb-text-condition-hint',
  templateUrl: './sb-text-condition-hint.component.html',
  styleUrls: ['./sb-text-condition-hint.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SbTextConditionHintComponent implements OnDestroy {
  private readonly conditions = inject(SbTextConditionService);

  /** The field's current text. */
  readonly text = input('');
  /** The field (its element or id), for Show and Edit. */
  readonly target = input<SbTextConditionTarget>(null);
  readonly disabled = input(false);
  /**
   * Variables (`#Key#`) every recipient must get: a warning names any that sits only inside
   * conditional text the conditions can leave out (not in every branch of an if/else), or only
   * where the render removes it for everyone.
   */
  readonly requiredTokens = input<string[]>([]);
  /** The variables the field can use: 'systemic' for a system email's field. */
  readonly variableScope = input<VariableScope>('campaign');
  /** The field's new text after Edit changed it. */
  readonly applied = output<string>();

  readonly context = signal<TextConditionContext | null>(null);
  private readonly hint = computed(() => this.describe(this.text(), this.requiredTokens(), this.context()));
  readonly problem = computed(() => this.hint().problem);
  readonly problemCount = computed(() => this.hint().problemCount);
  readonly summaries = computed(() => this.hint().summaries);
  readonly moreBlocks = computed(() => this.hint().moreBlocks);
  /** Required variables in the text that some recipients can go without. */
  readonly conditionalOnly = computed(() => this.hint().conditionalOnly);
  readonly quote = quote;
  private subscription = new Subscription();

  constructor() {
    // Each scope has its own variables: the first loads with the first check, a new one on change.
    effect(() => {
      const scope = this.variableScope();
      untracked(() => this.load(scope));
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /** Selects the problem in the field. */
  showProblem(): void {
    const field = this.resolve();
    const problem = this.problem();
    if (field && problem) {
      this.conditions.select(field, problem.start, Math.min(problem.end, (field.value || '').length));
    }
  }

  async edit(summary: BlockSummary): Promise<void> {
    const field = this.resolve();
    if (!field || this.disabled() || field.disabled || field.readOnly) {
      return;
    }
    const inside = summary.block.start + 1;
    const next = await this.conditions.edit(field, inside, inside, this.variableScope());
    if (next !== null) {
      this.applied.emit(next);
    }
  }

  private load(scope: VariableScope): void {
    this.subscription.unsubscribe();
    // Never check the text against the other scope's list while this one loads.
    this.context.set(null);
    this.subscription = this.conditions.context(scope).subscribe((context) => this.context.set(context));
  }

  private describe(textValue: string, requiredTokens: string[], context: TextConditionContext | null): TextHint {
    const text = textValue == null ? '' : String(textValue);
    if (!context || !text) {
      return NO_HINT;
    }
    const conditionalOnly = (requiredTokens || [])
      .filter((token) => hasVariableToken(text, token) && !this.conditions.alwaysHas(text, token, context));
    const scan = this.conditions.analyze(text, context);
    if (scan.problems.length) {
      return {...NO_HINT, problem: scan.problems[0], problemCount: scan.problems.length, conditionalOnly};
    }
    const outer = scan.blocks.filter((block) => block.depth === 1);
    const summaries: BlockSummary[] = [];
    outer.slice(0, MAX_SUMMARIES).forEach((block) => {
      const words = this.conditions.describe(text, block, context);
      if (words) {
        summaries.push({ block, text: words });
      }
    });
    return {...NO_HINT, summaries, moreBlocks: Math.max(0, outer.length - MAX_SUMMARIES), conditionalOnly};
  }

  private resolve(): HTMLInputElement | HTMLTextAreaElement | null {
    const target = this.target();
    if (typeof target === 'string') {
      return (document.getElementById(target) as HTMLInputElement | HTMLTextAreaElement) || null;
    }
    return target || null;
  }
}
